import {Alert} from '@mui/material'
import React, {useMemo, useState} from 'react'
import {useBoxesForSerialAdminQuery, useClearBoxSerialNumberMutation, useSetBoxSerialNumberMutation} from '../../../generated/graphql'
import {Box} from '../../../webui/Box'
import {Button} from '../../../webui/Button'
import {ButtonWithLoading} from '../../../webui/ButtonWithLoading'
import {MenuItem} from '../../../webui/inputs/MenuItem'
import {Select, SelectChangeEvent} from '../../../webui/inputs/Select'
import {TextField} from '../../../webui/inputs/TextField'
import {LoadingSpinnerContainer} from '../../../webui/progress/LoadingSpinnerContainer'
import {Typography} from '../../../webui/Typography'
import {resolveSerialErrorMessage} from './resolveSerialErrorMessage'
import {SerialNumberWarning} from './SerialNumberWarning'

interface InlineMessage {
  text: string
  severity: 'success' | 'error'
}

export const BoxSerialNumberPage: React.FC = () => {
  const {data, loading, error} = useBoxesForSerialAdminQuery()
  const [setSerial, {loading: setting}] = useSetBoxSerialNumberMutation()
  const [clearSerial, {loading: clearing}] = useClearBoxSerialNumberMutation()

  const [selectedBoxId, setSelectedBoxId] = useState('')
  const [serialInput, setSerialInput] = useState('')
  const [message, setMessage] = useState<InlineMessage | null>(null)

  const boxes = useMemo(() => [...(data?.box?.allBoxes ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [data?.box?.allBoxes])
  const selectedBox = boxes.find((box) => box.id === selectedBoxId)

  if (loading) {
    return <LoadingSpinnerContainer />
  }
  if (error) {
    return <div>{error.message}</div>
  }

  const onBoxChange = (event: SelectChangeEvent<string>) => {
    const id = event.target.value
    setSelectedBoxId(id)
    setSerialInput(boxes.find((box) => box.id === id)?.serialNumber ?? '')
    setMessage(null)
  }

  const onSet = async () => {
    if (!selectedBoxId) return
    setMessage(null)
    try {
      const response = await setSerial({variables: {boxId: selectedBoxId, serialNumber: serialInput.trim()}})
      const result = response.data?.setBoxSerialNumber
      if (result?.type === 'Success') {
        setSerialInput(result.box?.serialNumber ?? '')
        setMessage({text: 'Serial number saved.', severity: 'success'})
      } else {
        setMessage({text: resolveSerialErrorMessage(result?.type), severity: 'error'})
      }
    } catch (e) {
      setMessage({text: resolveSerialErrorMessage(undefined), severity: 'error'})
    }
  }

  const onClear = async () => {
    if (!selectedBoxId) return
    setMessage(null)
    try {
      const response = await clearSerial({variables: {boxId: selectedBoxId}})
      const result = response.data?.clearBoxSerialNumber
      if (result?.type === 'Success') {
        setSerialInput(result.box?.serialNumber ?? '')
        setMessage({text: 'Serial number cleared.', severity: 'success'})
      } else {
        setMessage({text: resolveSerialErrorMessage(result?.type), severity: 'error'})
      }
    } catch (e) {
      setMessage({text: resolveSerialErrorMessage(undefined), severity: 'error'})
    }
  }

  const busy = setting || clearing

  return (
    <Box m={2}>
      <SerialNumberWarning />
      <Box mt={2}>
        <Typography variant="h4">Box serial numbers</Typography>
      </Box>

      <Box mt={2}>
        <Select id="serial-box-select" label="Box" value={selectedBoxId} onChange={onBoxChange}>
          {boxes.map((box) => (
            <MenuItem key={box.id} value={box.id}>
              {box.name}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {selectedBox && (
        <>
          <Box mt={2}>
            <Typography>Current serial number: {selectedBox.serialNumber ? <strong>{selectedBox.serialNumber}</strong> : '(none)'}</Typography>
          </Box>

          <Box mt={2}>
            <TextField id="serial-input" label="Serial number" value={serialInput} onChange={(value) => setSerialInput(value)} />
          </Box>

          <Box display="flex" mt={3}>
            <ButtonWithLoading color="primary" loading={setting} onClick={onSet} disabled={busy || serialInput.trim() === ''} minWidth="120px">
              Set serial
            </ButtonWithLoading>
            <Box ml={2} />
            <Button onClick={onClear} disabled={busy || !selectedBox.serialNumber}>
              Clear
            </Button>
          </Box>
        </>
      )}

      {message && (
        <Box mt={3}>
          <Alert severity={message.severity}>{message.text}</Alert>
        </Box>
      )}
    </Box>
  )
}
